import { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../services/api';

export type LoginEmailStatus = { checking: boolean; available?: boolean };

export function useLoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailStatus, setEmailStatus] = useState<LoginEmailStatus>({ checking: false });
  const [isTouched, setIsTouched] = useState({ email: false });

  const { login, user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // AUTH-04 / CCT-9: honour the deep-link target captured by ProtectedRoute
  // in `location.state.from` so an external link into a protected route lands
  // there after login instead of always bouncing to /dashboard.
  const from =
    (location.state as { from?: { pathname?: string } } | null)?.from?.pathname ||
    '/dashboard';

  useEffect(() => {
    if (!authLoading && user) {
      navigate(from, { replace: true });
    }
  }, [user, authLoading, navigate, from]);

  // Live email check (debounced), same pattern as signup
  useEffect(() => {
    const trimmed = email.trim();
    if (!trimmed) {
      setEmailStatus({ checking: false, available: undefined });
      return;
    }
    const timer = setTimeout(async () => {
      if (!isTouched.email) return;
      setEmailStatus({ checking: true });
      try {
        const response = await authAPI.checkEmail(trimmed);
        setEmailStatus({ checking: false, available: response.available });
      } catch (err) {
        console.error('Error checking email:', err);
        setEmailStatus({ checking: false });
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [email, isTouched.email]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setError('');
    setIsTouched((prev) => ({ ...prev, email: true }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsTouched((prev) => ({ ...prev, email: true }));
    const trimmed = email.trim();
    if (!trimmed) return;

    setLoading(true);
    try {
      // Use live check result if we have it; otherwise check now
      let available = emailStatus.available;
      if (available === undefined) {
        const res = await authAPI.checkEmail(trimmed);
        available = res.available;
        setEmailStatus({ checking: false, available: res.available });
      }
      if (available) {
        setError('No account found with this email. Please sign up.');
        setLoading(false);
        return;
      }
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err: any) {
      const status = err.response?.status;
      const rawDetail = err.response?.data?.detail;
      const detail = typeof rawDetail === 'string' ? rawDetail : Array.isArray(rawDetail) ? rawDetail[0]?.msg ?? '' : '';
      const detailLower = (detail || '').toLowerCase();
      const isWrongCredentials =
        status === 401 ||
        detailLower.includes('password') ||
        detailLower.includes('username') ||
        detailLower.includes('wrong');
      const wrongCredsMessage = 'Either the password or username is wrong.';
      if (isWrongCredentials) {
        setError(wrongCredsMessage);
      } else if (status === 403) {
        setError(detail || 'Account is inactive. Please contact support.');
      } else {
        setError(detail || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return {
    email,
    setEmail,
    handleEmailChange,
    password,
    setPassword,
    showPassword,
    setShowPassword,
    error,
    loading,
    emailStatus,
    isTouched,
    setIsTouched,
    handleSubmit,
  };
}
